/**
 * Лимиты референсов, форматы и подсказки полей — чистые правила (25.09).
 *
 * Числа и форматы приходят с бэка из кода адаптера (/providers/capabilities →
 * Model.maxReferenceVideoSeconds, maxReferenceAudioSeconds, referenceVideoFormats,
 * referenceAudioFormats, imageLimits): у Seedance 2.5 видео и аудио до 30 с, у 2.0 до 15;
 * видео только mp4/mov, аудио только mp3/wav; фото 300–6000 px и не вытянутее 2,5:1.
 * Правило владельца: секунды референса режет только пользователь (тример в виджете),
 * сервер ничего не обрезает. Модель без чисел получает прежний запас (15 с) и не
 * проверяет фото — как было.
 */
import type { Model } from '../../../data/models'
import {
	SEEDANCE_25_DURATION,
	TREND_VIDEO_LABEL,
	followsTrendVideo,
	trendVideoAspect,
	trendVideoSeconds,
	trendVideoValue,
} from '../../../trend-video/rule'
import { imageRulesFor } from './imageInput'

export interface ImageLimits {
	minPx: number
	maxPx: number
	maxRatio: number
}

export const DEFAULT_REFERENCE_SECONDS = 15

export function totalSeconds(items: { duration?: number }[]): number {
	return items.reduce((sum, i) => sum + (i.duration || 0), 0)
}

/** Расширение файла без точки, в нижнем регистре; «quicktime» из MIME → mov. */
export function fileExtension(file: { name: string; type?: string }): string {
	const byName = (file.name || '').toLowerCase().split('.').pop() ?? ''
	if (byName && byName !== file.name.toLowerCase()) return byName
	const mime = (file.type || '').toLowerCase()
	if (mime === 'video/quicktime') return 'mov'
	return mime.split('/').pop() ?? ''
}

/** Файл в списке форматов модели; без списка — ограничений нет. */
export function formatAllowed(file: { name: string; type?: string }, formats?: string[] | null): boolean {
	if (!formats?.length) return true
	const ext = fileExtension(file)
	const aliases: Record<string, string[]> = { mov: ['mov', 'quicktime'], mp3: ['mp3', 'mpeg'], wav: ['wav', 'wave', 'x-wav'], jpeg: ['jpeg', 'jpg'] }
	return formats.some((f) => f.toLowerCase() === ext || (aliases[f.toLowerCase()] ?? []).includes(ext))
}

/** «mp4 или mov», «mp3, wav или aac». */
export function formatsLabel(formats: string[]): string {
	if (formats.length <= 1) return formats.join('')
	return `${formats.slice(0, -1).join(', ')} или ${formats[formats.length - 1]}`
}

/** Значение атрибута accept для <input type=file> по форматам модели; null — оставить дефолт компонента. */
export function acceptFor(formats?: string[] | null): string | null {
	if (!formats?.length) return null
	const mime: Record<string, string[]> = {
		mp4: ['video/mp4'], mov: ['video/quicktime'], webm: ['video/webm'],
		mp3: ['audio/mpeg', 'audio/mp3'], wav: ['audio/wav', 'audio/x-wav'], aac: ['audio/aac'], m4a: ['audio/mp4', 'audio/x-m4a'], ogg: ['audio/ogg'],
	}
	return formats.flatMap((f) => [...(mime[f.toLowerCase()] ?? []), `.${f.toLowerCase()}`]).join(',')
}

/** Kling Motion Control: движение человека с видео переносится на фото. Провайдер отказывает, если фигуры не
 *  видно (прод до 29.09: ~310 «No complete upper body detected», 55 «движение короче 3 с»). Проверить это
 *  автоматически нельзя — только подсказать в слоте. */
export function isMotionControlModel(model: Pick<Model, 'id' | 'backendModel'> | null | undefined): boolean {
	const key = `${model?.backendModel || ''} ${model?.id || ''}`.toLowerCase()
	return key.includes('motion-control') || key.includes('motion_control')
}

/** Рамки Kling для видео движения: от 3 до 30 с, при ориентации «По изображению» — до 10 (прод 01–30.09: 11 отказов на
 *  ролик короче 3 с и 5 — на «По изображению» длиннее 10 с, каждый после минут ожидания). Сервер держит те же рамки до
 *  списания (services/motion_control_input). */
export const MOTION_MIN_VIDEO_SECONDS = 3
export const MOTION_IMAGE_MAX_SECONDS = 10
export const MOTION_VIDEO_MAX_SECONDS = 30

/** Секунды ролика движения → целые секунды оплаты при ориентации «По видео» (итог равен длине ролика): округление,
 *  как при загрузке, в рамках 3–30 с — как сервер (services/motion_control_input.billing_seconds). */
export function motionBillingSeconds(seconds: number): number {
	return Math.max(MOTION_MIN_VIDEO_SECONDS, Math.min(MOTION_VIDEO_MAX_SECONDS, Math.round(seconds)))
}

/** Текст отказа до загрузки для ролика короче 3 с или null. */
export function motionVideoTooShort(seconds: number): string | null {
	if (!Number.isFinite(seconds) || seconds >= MOTION_MIN_VIDEO_SECONDS) return null
	return `Видео движения короче 3 секунд (${secondsLabel(Math.round(seconds * 10) / 10)} с). Kling принимает ролики от 3 до 30 секунд — загрузите ролик подлиннее.`
}

/** Выбранная длительность после смены ориентации: «По изображению» — не больше 10 с. */
export function motionDurationForOrientation(orientation: string, selected: number): number {
	return orientation === 'image' ? Math.min(selected, MOTION_IMAGE_MAX_SECONDS) : selected
}

export const MOTION_PHOTO_NOTE = 'В кадре — человек по пояс или целиком: голова, плечи и руки видны, ничего не обрезано.'
export const MOTION_VIDEO_NOTE =
	'Видео движения: один человек по пояс или целиком, один план без склеек, камера стоит, движения плавные. Kling ' +
	'переносит только непрерывный кусок — при склейках или движении камеры ролик выйдет короче, вплоть до 3 с. ' +
	'mp4 или mov, до 100 МБ, 3–30 с.'

/** Замена человека в ролике (Wan 2.2 Animate Replace, P-Video-Replace — 10.10): человека в ролике заменяет аватар с
 *  фото, всё остальное — из ролика. Рамки ролика — 2–30 с; сервер держит те же до списания (services/video_replace_input,
 *  там же REPLACE_MODEL_KEYS — тот же список). */
export const REPLACE_MODEL_KEYS = ['animate-replace', 'p-video-replace'] as const

export function isVideoReplaceModel(model: Pick<Model, 'id' | 'backendModel'> | null | undefined): boolean {
	const key = `${model?.backendModel || ''} ${model?.id || ''}`.toLowerCase()
	return REPLACE_MODEL_KEYS.some((k) => key.includes(k))
}

export const REPLACE_MIN_VIDEO_SECONDS = 2
export const REPLACE_MAX_VIDEO_SECONDS = 30

/** Секунды ролика → целые секунды оплаты (итог равен длине ролика): округление в рамках 2–30 с — как сервер
 *  (services/video_replace_input.billing_seconds). */
export function replaceBillingSeconds(seconds: number): number {
	return Math.max(REPLACE_MIN_VIDEO_SECONDS, Math.min(REPLACE_MAX_VIDEO_SECONDS, Math.round(seconds)))
}

/** Текст отказа до загрузки для ролика короче 2 с или null — тот же, что у сервера. */
export function replaceVideoTooShort(seconds: number): string | null {
	if (!Number.isFinite(seconds) || seconds >= REPLACE_MIN_VIDEO_SECONDS) return null
	return `Ролик короче 2 секунд (${secondsLabel(Math.round(seconds * 10) / 10)} с). Замена человека в ролике работает с роликами от 2 до 30 секунд — загрузите ролик подлиннее.`
}

export const REPLACE_PHOTO_NOTE = 'Аватар встанет на место человека в ролике: один человек, лицо хорошо видно, лучше в полный рост.'
export const REPLACE_VIDEO_NOTE =
	'Ролик: один человек, от 2 до 30 секунд. Его заменим аватаром — движение, фон, камера, свет и звук останутся.'

export function isSeedance25Model(model: Pick<Model, 'id' | 'backendModel'> | null | undefined): boolean {
	const key = `${model?.backendModel || ''} ${model?.id || ''}`.toLowerCase()
	return key.includes('seedance-2-5') || key.includes('seedance-2.5')
}

/** Рамки для длины по референсу — секунды самой модели (код адаптера), как режет сервер
 *  (seedance_refs.reference_seconds_for_billing). Список админа сужает только слайдер: 26.09 у
 *  Seedance 2.5 карточка начиналась с 5 с, пилюля показывала 5, а сервер списывал 4. */
function durationBounds(model: Model): { min: number; max: number } {
	if (model.providerDurationRange) return { min: model.providerDurationRange[0], max: model.providerDurationRange[1] }
	const nums = (model.durations ?? []).map(Number).filter((n) => Number.isFinite(n) && n > 0)
	return { min: nums.length ? Math.min(...nums) : 4, max: nums.length ? Math.max(...nums) : 30 }
}

/**
 * Намерение при видео-референсе (Seedance 2.5) — разбор 25.09: модель сама решает по
 * промпту, редактирование это или новое видео по референсу; для редактирования провайдер
 * принимает только duration = -1 (ролик выходит длиной входа), а при -1 в обычной
 * генерации длину выбирает модель сама (референс 25 с → ролик 8 с, списано за 25).
 * Поэтому намерение задаёт юзер: «edit» — Переделать видео (длина заперта на референсе,
 * оплата по ней), «new» — Новое по референсу (длительность уходит явно, оплата по ней).
 * Формат кадра: у «edit» его по правилу модели задаёт видео, у «new» уходит выбранный (11.10).
 */
export type VideoIntent = 'edit' | 'new'

export const VIDEO_INTENT_OPTIONS: { value: VideoIntent; label: string }[] = [
	{ value: 'edit', label: 'Переделать видео' },
	{ value: 'new', label: 'Новое по референсу' },
]

export const VIDEO_INTENT_HINTS: Record<VideoIntent, string> = {
	edit: 'Модель меняет ваш ролик по промпту. Длина и формат кадра — как у референса, оплата по его длине.',
	new: 'Модель снимает новый ролик, беря из референса движение и стиль. Длину и формат кадра выбираете вы.',
}

/** Подсказка у переключателя «Черновик» (Seedance 2.5): что это, чем кончается и чего не принимает. */
export const DRAFT_HINT =
	'Черновик — быстрая и недорогая проба в 480p. Понравился — доведите его до финала 1080p кнопкой на ролике. ' +
	'Финал повторяет сюжет и движение, но кадр, ракурс и свет могут отличаться. Из одного черновика получается один финал, ' +
	'хранится черновик 7 суток. Фото с реальными людьми, видео и аудио в черновик не принимаются.'
/** Короткая заметка рядом с настройками, пока черновик включён. */
export const NOTE_DRAFT = 'Черновик: проба в 480p. Финал 1080p делается из него кнопкой на ролике — ракурс и свет могут отличаться.'
export const DRAFT_RESOLUTION_LABEL = '480p'

export const ASPECT_FOLLOWS_LABEL = 'Как у референса'
export const NOTE_ASPECT_FOLLOWS = 'Seedance переделывает видео: формат кадра берётся с него, своё соотношение сторон выбрать нельзя.'

export function videoIntentOf(settings: { videoIntent?: string } | null | undefined): VideoIntent {
	return settings?.videoIntent === 'new' ? 'new' : 'edit'
}

/** Свои ролики юзера — без референса карточки «под капотом» (он всегда идёт как «edit»). */
export function ownReferenceVideos<T extends { cardRef?: boolean }>(videos: T[] | null | undefined): T[] {
	return (videos ?? []).filter((v) => !v.cardRef)
}

type TrendVideoSettings = {
	seedanceMode?: string
	cardTrendVideo?: { hidden: boolean; removed?: boolean }
	cardReferenceSeconds?: number
	cardReferenceFrame?: { width: number; height: number }
}

/**
 * Видео тренда переделывается — общее правило сайта и админки (shared/trend-video) на живом состоянии виджета: что знает
 * карточка (settings.cardTrendVideo: видео есть, скрыто ли, снял ли его человек из слота — ставит виджет тренда) и
 * положил ли человек своё видео. Тогда формат и длина ролика — как у видео: полей нет, вместо них строка.
 */
export function trendVideoFollows(
	model: Pick<Model, 'id' | 'backendModel'> | null | undefined,
	settings: TrendVideoSettings,
	referenceVideos: { cardRef?: boolean }[],
): boolean {
	const trend = settings.cardTrendVideo
	return !!trend && followsTrendVideo({
		model: model?.backendModel || model?.id,
		mode: settings.seedanceMode,
		trendVideo: true,
		videoHidden: trend.hidden,
		videoRemoved: !!trend.removed,
		ownVideo: ownReferenceVideos(referenceVideos).length > 0,
	})
}

/** Длина ролика по видео тренда (замер сервера) в пределах Seedance 2.5 — столько спишет сервер; null — правило не
 *  действует или длина неизвестна. */
function trendVideoSecondsOf(model: Model | null | undefined, settings: TrendVideoSettings, referenceVideos: { cardRef?: boolean }[]): number | null {
	if (!model || !trendVideoFollows(model, settings, referenceVideos)) return null
	return trendVideoSeconds(settings.cardReferenceSeconds, SEEDANCE_25_DURATION[0], SEEDANCE_25_DURATION[1])
}

function trendVideoAspectOf(settings: TrendVideoSettings) {
	const frame = settings.cardReferenceFrame
	return frame ? trendVideoAspect(frame.width, frame.height) : null
}

/** Строка вместо полей формата и длины: label «Формат и длина», value «как у видео тренда: 16:9, 15 с», line — целиком
 *  через тире. null — правило не действует, поля на месте. */
export function trendVideoNote(
	model: Model | null | undefined,
	settings: TrendVideoSettings,
	referenceVideos: { cardRef?: boolean }[],
): { label: string; value: string; line: string } | null {
	if (!model || !trendVideoFollows(model, settings, referenceVideos)) return null
	const value = trendVideoValue(trendVideoAspectOf(settings), trendVideoSecondsOf(model, settings, referenceVideos))
	return { label: TREND_VIDEO_LABEL, value, line: `${TREND_VIDEO_LABEL} — ${value}` }
}

/** Формат в сводке настроек: «как у видео 16:9» (кадр ещё не замерен — «как у видео»). */
export function trendVideoAspectLabel(settings: TrendVideoSettings): string {
	const aspect = trendVideoAspectOf(settings)
	return aspect ? `как у видео ${aspect.exact ? '' : '≈'}${aspect.aspect}` : 'как у видео'
}

/**
 * Формат кадра задаёт видео: Seedance 2.5 в «Референсах» ПЕРЕДЕЛЫВАЕТ видео — своё в «Переделать видео» или видео
 * тренда (trendVideoFollows; скрытое от пользователя подставит сервер). По правилу модели формат и длина тогда берутся
 * с видео, и сервер шлёт adaptive. Кадры, трек и «Новое по референсу» формат не трогают — уходит выбранный (11.10:
 * раньше сервер менял его и там, а пилюля молчала).
 */
export function aspectFollowsReference(
	model: Pick<Model, 'id' | 'backendModel'> | null | undefined,
	settings: { seedanceMode?: string; videoIntent?: string } & TrendVideoSettings,
	referenceVideos: { url?: string; duration?: number; cardRef?: boolean }[],
): boolean {
	if (!model || !isSeedance25Model(model)) return false
	if ((settings.seedanceMode ?? 'frames') === 'frames') return false
	if (ownReferenceVideos(referenceVideos).length) return videoIntentOf(settings) !== 'new'
	return referenceVideos.length > 0 || trendVideoFollows(model, settings, referenceVideos)
}

/** «Переделать видео» работает с одним роликом — текст ошибки до запроса или null. */
export function videoIntentProblem(
	model: Pick<Model, 'id' | 'backendModel'> | null | undefined,
	settings: { seedanceMode?: string; videoIntent?: string },
	referenceVideos: { url?: string; duration?: number; cardRef?: boolean }[],
): string | null {
	if (!model || !isSeedance25Model(model)) return null
	if ((settings.seedanceMode ?? 'frames') === 'frames') return null
	if (videoIntentOf(settings) !== 'edit') return null
	if (ownReferenceVideos(referenceVideos).length <= 1) return null
	return 'Режим «Переделать видео» работает с одним роликом. Оставьте один или выберите «Новое по референсу».'
}

/** Секунды для человека: целые как есть, дробные с одним знаком («25,1 с»). */
export function secondsLabel(seconds: number): string {
	return Number.isInteger(seconds) ? String(seconds) : seconds.toFixed(1).replace('.', ',')
}

/**
 * Длина ролика следует за референсом: Seedance 2.5 в режиме «Референсы», намерение
 * «Переделать видео», ОДНО видео — ролик выходит ровно такой длины (у провайдера
 * duration = -1); видео тренда — по общему правилу (trendVideoFollows). Возвращает секунды
 * для пилюли, цены и запроса, либо null, когда правило не действует (другая модель, режим
 * «Кадры», «Новое по референсу», несколько роликов, длина ещё не известна).
 */
export function durationFollowsReference(
	model: Model | null | undefined,
	settings: { seedanceMode?: string; videoIntent?: string } & TrendVideoSettings,
	referenceVideos: { duration?: number; url?: string; cardRef?: boolean }[],
): number | null {
	if (!model || !isSeedance25Model(model)) return null
	// Видео тренда переделывается (shared/trend-video): длина — замер видео на сервере, столько же он и спишет.
	const trendSec = trendVideoSecondsOf(model, settings, referenceVideos)
	if (trendSec != null) return trendSec
	if ((settings.seedanceMode ?? 'frames') === 'frames') return null
	// «Снять новое по референсу»: длительность уходит явно, замка нет.
	if (videoIntentOf(settings) === 'new') return null
	// Своё видео главнее видео тренда: сервер кладёт видео тренда только в пустой канал и переделывает своё — считаем
	// только свои ролики (иначе видео тренда в слоте давало «два ролика», пилюля не запиралась, а сервер списывал по
	// длине своего видео).
	const own = ownReferenceVideos(referenceVideos)
	const videos = own.length ? own : referenceVideos
	if (videos.length !== 1) return null
	const { min, max } = durationBounds(model)
	// 25,04 с из-за упаковки кадров — это те же 25 (округление общее с видео тренда и сервером).
	return trendVideoSeconds(videos[0].duration, min, max)
}

export const NOTE_DURATION_FOLLOWS =
	'Переделка видео: длина ролика равна длине референса. Нужна своя длина — выберите «Новое по референсу», нужно короче — обрежьте референс.'

export const NOTE_DURATION_TREND = 'Длительность задана трендом: с другой длиной тренд не получится.'

/**
 * Длительность под замком — одно правило для пилюли, шторки, сводки и запроса: длина по видео-референсу (переделка,
 * durationFollowsReference) или длина тренда (замок в карточке тренда — settings.cardDurationLock, 11.10). Видео
 * главнее: где длину задаёт видео (свой ролик, видео тренда, вход Motion Control — hasVideoInput), замок тренда не
 * действует — так же решает сервер (api/v1/video._trend_card_checked). null — длительность выбирает человек.
 */
export function durationLock(
	model: Model | null | undefined,
	settings: { seedanceMode?: string; videoIntent?: string; cardDurationLock?: number } & TrendVideoSettings,
	referenceVideos: { duration?: number; url?: string; cardRef?: boolean }[],
	hasVideoInput = false,
): { sec: number; note: string; byTrend: boolean } | null {
	const follow = durationFollowsReference(model, settings, referenceVideos)
	if (follow != null) return { sec: follow, note: NOTE_DURATION_FOLLOWS, byTrend: false }
	const trend = settings.cardDurationLock
	if (trend == null || !Number.isFinite(trend) || trend <= 0) return null
	// У тренда с видео замка длительности нет — так же решает сервер (video._trend_card_checked): длину задаёт видео,
	// а убрал его человек — длину выбирает он.
	if (hasVideoInput || referenceVideos.length > 0 || settings.cardTrendVideo) return null
	return { sec: trend, note: NOTE_DURATION_TREND, byTrend: true }
}

/**
 * Подсказка к полю по лимитам модели (форматы, секунды, размер файла). Пусто — у модели
 * нет своих чисел, поле показывает общую подсказку. Ручная подсказка админа (paramHints)
 * всегда главнее — её подставляет UploadsRow до вызова.
 */
export function slotHint(key: string, model: Model | null | undefined, siteMaxMb?: { video?: number; audio?: number; image?: number }): string | undefined {
	if (!model) return undefined
	const sizePart = (modelMb: number | undefined, site: number | undefined): string | null => {
		const n = [modelMb, site].filter((v): v is number => typeof v === 'number' && v > 0)
		return n.length ? `до ${Math.min(...n)} МБ` : null
	}
	const line = (head: string, details: (string | null)[], tail?: string): string =>
		`${head}: ${details.filter((d): d is string => !!d).join(', ')}.${tail ? ` ${tail}` : ''}`

	if (key === 'reference_videos' || key === 'reference_video') {
		const sec = model.maxReferenceVideoSeconds
		const formats = model.referenceVideoFormats
		if (!sec && !formats?.length) return undefined
		const many = key === 'reference_videos'
		return line(
			'Видео-референс',
			[
				formats?.length ? `только ${formatsLabel(formats)}` : null,
				sec ? `от ${model.minReferenceVideoSeconds ?? 2} до ${sec} с${many ? ` (все ролики вместе не больше ${sec} с)` : ''}` : null,
				sizePart(model.referenceVideoMaxMb, siteMaxMb?.video),
			],
			sec ? 'Ролик длиннее откроет выбор отрезка.' : undefined,
		)
	}
	if (key === 'reference_audios') {
		const sec = model.maxReferenceAudioSeconds
		const formats = model.referenceAudioFormats
		if (!sec && !formats?.length) return undefined
		return line(
			'Аудио-референс',
			[
				formats?.length ? `только ${formatsLabel(formats)}` : null,
				sec ? `от 2 до ${sec} с (все файлы вместе не больше ${sec} с)` : null,
				sizePart(model.referenceAudioMaxMb, siteMaxMb?.audio),
			],
			sec ? 'Файл длиннее откроет выбор отрезка.' : undefined,
		)
	}
	if (key === 'video' && isMotionControlModel(model)) return MOTION_VIDEO_NOTE
	if (key === 'video' && isVideoReplaceModel(model)) return REPLACE_VIDEO_NOTE
	if (key === 'image' || key === 'reference_images' || key === 'end_image') {
		// Рамки — те же, по которым фото подгоняется при загрузке (utils/imageInput). Вес не пишем: тяжёлое сожмём сами.
		const lim = imageRulesFor(model)
		if (!lim) return undefined
		const head = key === 'reference_images' ? 'Фото-референсы' : key === 'end_image' ? 'Последний кадр' : 'Фото'
		const hint = line(
			head,
			['jpeg, png, webp или HEIC', `стороны от ${lim.minPx} до ${lim.maxPx} px, не вытянутее ${String(lim.maxRatio).replace('.', ',')}:1`],
			'Большое уменьшим, чуть меньшее или чуть вытянутое подгоним сами.',
		)
		if (key === 'image' && isMotionControlModel(model)) return `${hint} ${MOTION_PHOTO_NOTE}`
		return key === 'image' && isVideoReplaceModel(model) ? `${hint} ${REPLACE_PHOTO_NOTE}` : hint
	}
	return undefined
}
